import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Leaderboard from '../../components/rounds/Leaderboard';
import { roundsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';

const POLL_INTERVAL = 15000;

/**
 * Home / Leaderboard tab — shows the leaderboard directly, same as TML's
 * Leaderboard tab and FriDave's home page: no round list, no admin controls,
 * just the current or most recently played round's table. Round setup/start/
 * finish/delete live on Manage Events (reached via the admin hamburger).
 */
export default function ClubhouseLanding() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [round, setRound] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const all = await roundsAPI.getAll({ eventType: 'open' });
      const list = Array.isArray(all) ? all : [];
      // Server returns rounds ORDER BY start_date DESC, so after filtering,
      // the first entry is the most recent — same logic as FriDaveLanding.
      const eligible = list.filter(r => r.status === 'in_progress' || r.status === 'active' || r.status === 'completed');
      const active = eligible.find(r => r.status === 'in_progress' || r.status === 'active');
      const current = active || eligible[0] || null;

      if (current) {
        const lb = await roundsAPI.getLeaderboard(current.id).catch(() => []);
        setRound(current);
        setLeaderboard(Array.isArray(lb) ? lb : []);
      } else {
        setRound(null);
        setLeaderboard([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while live so the table updates without a manual refresh
  useEffect(() => {
    if (!round || !(round.status === 'in_progress' || round.status === 'active')) return;
    const interval = setInterval(async () => {
      const lb = await roundsAPI.getLeaderboard(round.id).catch(() => null);
      if (lb) setLeaderboard(Array.isArray(lb) ? lb : []);
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [round]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4">
        <p className="text-error font-semibold">Could not load leaderboard: {error}</p>
      </div>
    );
  }

  if (!round) {
    return (
      <div className="px-4">
        <div className="bg-ch-surface rounded-2xl border border-ch-secondary/15 p-8 text-center">
          <p className="text-base text-ch-secondary mb-1">No rounds yet</p>
          {isAuthenticated && (
            <button
              onClick={() => navigate(ROUTES.CLUBHOUSE_ROUNDS)}
              className="text-sm font-semibold text-ch-accent mt-3"
            >
              Manage Events →
            </button>
          )}
        </div>
      </div>
    );
  }

  const isLive = round.status === 'in_progress' || round.status === 'active';
  const roundLabel = round.round_type === 'practice' ? 'Practice Round'
    : round.round_type === 'championship' ? 'Championship Round'
    : 'Round';

  return (
    <div>
      <div className="px-4 mb-4">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl font-bold text-ch-primary">{round.event_name}</h1>
          <span className={`shrink-0 px-3 py-1 text-xs font-semibold rounded-full ${
            isLive ? 'bg-green-100 text-green-700' : 'bg-ch-secondary/10 text-ch-secondary/70'
          }`}>
            {isLive ? 'In Progress' : 'Completed'}
          </span>
        </div>
        <p className="text-sm text-ch-secondary/70 mt-0.5">
          {round.course_name} · {roundLabel}
        </p>
      </div>

      <Leaderboard leaderboard={leaderboard} round={round} roundId={round.id} />
    </div>
  );
}
